import React, { createContext, useContext, useMemo } from "react";
import { DesignStaticProvider } from "../../../context/DesignContext";
import type { ThemeTokens } from "./types";
import { templateDesignStaticProps } from "./resolveTheme";
import { templateFeedSurfaceCssProperties } from "./websiteTemplateSurface";
import { useTemplateStyles } from "./useTemplateStyles";

/** True under Website Home / LiB surfaces — prefer template bones over classic DataSkeleton. */
const WebsiteTemplateLoadersCtx = createContext(false);

export function useWebsiteTemplateLoaders(): boolean {
  return useContext(WebsiteTemplateLoadersCtx);
}

/** Context-only wrap when full {@link TemplateFeedDesignBoundary} chrome is already present. */
export function WebsiteTemplateLoadersProvider({
  children,
  enabled = true,
}: {
  children: React.ReactNode;
  enabled?: boolean;
}) {
  return (
    <WebsiteTemplateLoadersCtx.Provider value={enabled}>
      {children}
    </WebsiteTemplateLoadersCtx.Provider>
  );
}

export interface TemplateFeedDesignBoundaryProps {
  T: ThemeTokens;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

/**
 * Isolates LiB feed/search subtrees from app-wide `DesignProvider` (link-in-bio profile design).
 * Sets template palette CSS vars + `DesignStaticProvider` for `useDesign()` / PostInfo contrast.
 */
export function TemplateFeedDesignBoundary({
  T,
  className,
  style,
  children,
}: TemplateFeedDesignBoundaryProps) {
  const surfaceStyle = useMemo(
    () => ({
      ...templateFeedSurfaceCssProperties(T),
      ...style,
    }),
    [T, style],
  );
  const surfaceRef = useTemplateStyles<HTMLDivElement>(surfaceStyle);

  return (
    <div ref={surfaceRef} className={className}>
      <WebsiteTemplateLoadersCtx.Provider value={true}>
        <DesignStaticProvider {...templateDesignStaticProps(T)}>
          {children}
        </DesignStaticProvider>
      </WebsiteTemplateLoadersCtx.Provider>
    </div>
  );
}
